import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * A row that scrolls sideways, with an arrow at each end to do it with.
 *
 * A bare overflow row gives no sign that more of it exists and no way to move it
 * but a trackpad or a swipe: on a till with a mouse, a tenth open ticket was
 * simply out of reach. The arrows appear only on the side that has more to show,
 * so a row that fits shows none and one scrolled to its end loses that end's.
 *
 * `activeKey` (whatever identifies the selected item) keeps that item in view when
 * the selection changes - picking a ticket from the far end of the list should
 * not leave it half off the screen. It runs on a change of selection and no other
 * time: following it on every render would pull the row back to the selected item
 * each time somebody scrolled away from it.
 *
 * `className` goes on the scrolling row itself, so the existing look of a row
 * (`tabs`, `ticket-strip`) is untouched; this only wraps it.
 */
export function ScrollStrip({
  children,
  className = '',
  label,
  activeKey,
}: {
  children: ReactNode;
  className?: string;
  /** Names the row for assistive technology: "Open tickets", "Categories". */
  label: string;
  /** Changes when the selected item does; omit to leave the scroll position alone. */
  activeKey?: string | number | null;
}) {
  const track = useRef<HTMLDivElement>(null);
  const [canStart, setCanStart] = useState(false);
  const [canEnd, setCanEnd] = useState(false);

  const measure = useCallback(() => {
    const el = track.current;
    if (!el) return;
    // A pixel of slack: scroll positions are fractional on a scaled display.
    setCanStart(el.scrollLeft > 1);
    setCanEnd(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = track.current;
    if (!el) return;
    measure();
    const resize = new ResizeObserver(measure);
    resize.observe(el);
    // Items added or removed change the width without resizing the row.
    const mutate = new MutationObserver(measure);
    mutate.observe(el, { childList: true, subtree: true });
    return () => {
      resize.disconnect();
      mutate.disconnect();
    };
  }, [measure]);

  useEffect(() => {
    if (activeKey === undefined) return;
    const el = track.current;
    const active = el?.querySelector<HTMLElement>('.active');
    if (!el || !active) return;
    // Scrolled by hand on the row alone: scrollIntoView would also move the page.
    const left = active.offsetLeft - el.offsetLeft;
    if (left < el.scrollLeft) el.scrollTo({ left: Math.max(0, left - 24), behavior: 'smooth' });
    else if (left + active.offsetWidth > el.scrollLeft + el.clientWidth) {
      el.scrollTo({ left: left + active.offsetWidth - el.clientWidth + 24, behavior: 'smooth' });
    }
    // Only when the selection changes: see the note above.
  }, [activeKey]);

  const page = (direction: -1 | 1) =>
    track.current?.scrollBy({
      left: direction * Math.max(120, (track.current?.clientWidth ?? 0) * 0.8),
      behavior: 'smooth',
    });

  return (
    <div className="scroll-strip">
      {canStart ? (
        <button
          type="button"
          className="scroll-arrow start"
          aria-label={`Scroll ${label} left`}
          onClick={() => page(-1)}
        >
          ‹
        </button>
      ) : null}
      <div
        ref={track}
        className={`scroll-strip-track ${className}`}
        role="group"
        aria-label={label}
        onScroll={measure}
      >
        {children}
      </div>
      {canEnd ? (
        <button
          type="button"
          className="scroll-arrow end"
          aria-label={`Scroll ${label} right`}
          onClick={() => page(1)}
        >
          ›
        </button>
      ) : null}
    </div>
  );
}
